import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHeadset,
  faPhone,
  faEnvelope,
  faLocationDot,
  faKey,
  faExternalLinkAlt,
} from '@fortawesome/free-solid-svg-icons';
import styles from './PreguntasPage.module.css';
import data from './preguntas_frecuentes.json';
import { PageHeader, Input } from '../../../components/ui';
import { Reveal } from '../../../components/motion';
import { useAuth } from '../../../context/authContext';
import {
  buildSupportTicketUrl,
  SUPPORT_CONTACTS,
} from '../../../services/supportTicket';

const PreguntasPage = () => {
  const [openItems, setOpenItems] = useState({});
  const [query, setQuery] = useState('');
  const { isAuthenticated, user, profile } = useAuth();

  const email = profile?.email || user?.email || '';
  const userId = user?.id ?? profile?.id ?? null;

  const supportUrl = useMemo(
    () =>
      buildSupportTicketUrl({
        email: isAuthenticated ? email : undefined,
        userId: isAuthenticated ? userId : undefined,
      }),
    [isAuthenticated, email, userId]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const all = data.preguntas_frecuentes.map((item, idx) => ({ item, idx }));
    if (!q) return all;
    return all.filter(
      ({ item }) =>
        item.pregunta.toLowerCase().includes(q) ||
        item.respuesta.toLowerCase().includes(q) ||
        String(item.categoria || "")
          .toLowerCase()
          .includes(q)
    );
  }, [query]);

  const groupedFaq = useMemo(() => {
    const groups = [];
    let current = null;
    filtered.forEach(({ item, idx }) => {
      const cat = item.categoria || "Preguntas frecuentes";
      if (!current || current.categoria !== cat) {
        current = { categoria: cat, items: [] };
        groups.push(current);
      }
      current.items.push({ item, idx });
    });
    return groups;
  }, [filtered]);

  const toggleItem = (index) => {
    setOpenItems((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  return (
    <div className={styles.page}>
      <PageHeader title="Ayuda" />

      <div className={styles.layout}>
        {/* Reportar un problema */}
        <Reveal as="section" delay={0} className={styles.card}>
          <div className={styles.cardHeader}>
            <FontAwesomeIcon icon={faHeadset} className={styles.cardIcon} />
            <div>
              <h2 className={styles.cardTitle}>Reportar un problema</h2>
              <p className={styles.cardHint}>
                Genera un ticket de soporte. Se abre en una pestaña nueva.
                {isAuthenticated
                  ? ' Usaremos tu correo para agilizar la atención.'
                  : ' Si no estás logueado, completa tu correo en el formulario.'}
              </p>
            </div>
          </div>

          <div className={styles.cardActions}>
            <a
              href={supportUrl}
              className={styles.primaryBtn}
              target="_blank"
              rel="noopener noreferrer"
            >
              Crear ticket
              <FontAwesomeIcon icon={faExternalLinkAlt} />
            </a>
          </div>
        </Reveal>

        {/* Contáctanos */}
        <Reveal as="section" delay={0.06} className={styles.card}>
          <h2 className={styles.cardTitle}>Contáctanos</h2>
          <ul className={styles.contactList}>
            <li>
              <a href={SUPPORT_CONTACTS.phoneTel} className={styles.contactLink}>
                <FontAwesomeIcon icon={faPhone} />
                <span>{SUPPORT_CONTACTS.phoneDisplay}</span>
              </a>
            </li>
            <li>
              <a
                href={`mailto:${SUPPORT_CONTACTS.premiosEmail}`}
                className={styles.contactLink}
              >
                <FontAwesomeIcon icon={faEnvelope} />
                <span>Premios: {SUPPORT_CONTACTS.premiosEmail}</span>
              </a>
            </li>
            <li>
              <a
                href={`mailto:${SUPPORT_CONTACTS.soporteEmail}`}
                className={styles.contactLink}
              >
                <FontAwesomeIcon icon={faEnvelope} />
                <span>Mail: {SUPPORT_CONTACTS.soporteEmail}</span>
              </a>
            </li>
            <li>
              <a
                href={SUPPORT_CONTACTS.mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.contactLink}
              >
                <FontAwesomeIcon icon={faLocationDot} />
                <span>{SUPPORT_CONTACTS.address}</span>
              </a>
            </li>
          </ul>
        </Reveal>

        {/* Olvidé mi clave */}
        <Reveal as="section" delay={0.12} className={styles.card}>
          <div className={styles.cardHeader}>
            <FontAwesomeIcon icon={faKey} className={styles.cardIcon} />
            <div>
              <h2 className={styles.cardTitle}>Olvidé mi clave</h2>
              <p className={styles.cardHint}>
                Recupera tu PIN por correo, igual que en la app (código de
                verificación y nuevo PIN).
              </p>
            </div>
          </div>
          <Link
            to="/auth"
            state={{ mode: 'forgot' }}
            className={styles.primaryBtn}
          >
            Recuperar PIN
          </Link>
        </Reveal>

        {/* Preguntas frecuentes */}
        <Reveal as="section" delay={0.18} className={styles.faqSection}>
          <h2 className={styles.sectionTitle}>Preguntas frecuentes</h2>
          <div className={styles.searchWrap}>
            <Input
              label="Buscar"
              placeholder="Escribe tu duda…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className={styles.content}>
            {filtered.length === 0 ? (
              <p className={styles.noResults}>No hay resultados para tu búsqueda.</p>
            ) : (
              groupedFaq.map((group) => (
                <div key={group.categoria} className={styles.faqGroup}>
                  <h3 className={styles.faqCategory}>{group.categoria}</h3>
                  {group.items.map(({ item, idx }) => (
                    <div key={idx} className={styles.faqItem}>
                      <button
                        type="button"
                        className={`${styles.question} ${
                          openItems[idx] ? styles.questionOpen : ""
                        }`}
                        onClick={() => toggleItem(idx)}
                        aria-expanded={Boolean(openItems[idx])}
                      >
                        {item.pregunta}
                        <span className={styles.chevron}>
                          {openItems[idx] ? "−" : "+"}
                        </span>
                      </button>
                      <div
                        className={`${styles.answerBase} ${
                          openItems[idx]
                            ? styles.answerOpen
                            : styles.answerClosed
                        }`}
                      >
                        {String(item.respuesta || "")
                          .split("\n")
                          .map((line, lineIdx) => (
                            <p key={lineIdx} className={styles.answerLine}>
                              {line || "\u00A0"}
                            </p>
                          ))}
                      </div>
                    </div>
                  ))}
                </div>
              ))
            )}
          </div>
        </Reveal>
      </div>
    </div>
  );
};

export default PreguntasPage;
